import { API_URL } from "env";
import { mixLogOut } from "../services/mixin";

/**
 * API Thư viện hình ảnh (Photo Library).
 *  - getPhotoLibraries: GET    /api/v1/photo_libraries     (name, directory_id, type, limit, page)
 *  - uploadPhotoLibrary: POST  /api/v1/photo_library       (multipart: image, name, directory_id)
 *  - deletePhotoLibrary: DELETE /api/v1/photo_library/{id}
 *  - getPhotoDirectories: GET  /api/v1/photo_directories   (danh sách thư mục con — best-effort)
 */

export interface PhotoItem {
    id: number;
    name: string;
    image: string;
    type: string;
    directory_id: number | null;
    created_at?: string;
    updated_at?: string;
}

export interface PhotoListData {
    total: number;
    current_page: number;
    items: PhotoItem[];
}

export interface PhotoListResponse {
    status: boolean;
    data: PhotoListData | null;
    msg?: string;
}

const authHeaders = () => ({
    Accept: "application/json",
    Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

export async function getPhotoLibraries(
    params: { name?: string; directory_id?: number; type?: string; limit?: number; page?: number } = {}
): Promise<PhotoListResponse> {
    try {
        const q = new URLSearchParams();
        if (params.name) q.append("name", params.name);
        if (params.directory_id) q.append("directory_id", String(params.directory_id));
        if (params.type) q.append("type", params.type);
        if (params.limit) q.append("limit", String(params.limit));
        if (params.page) q.append("page", String(params.page));

        const res = await fetch(`${API_URL}/api/v1/photo_libraries?${q.toString()}`, {
            headers: authHeaders(),
        });
        if (!res.ok) {
            if (res.status === 401) { mixLogOut(); }
            throw new Error(`HTTP ${res.status}`);
        }
        const json = await res.json();
        return { status: !!json?.status, data: json?.data ?? null };
    } catch (error) {
        console.error("Failed to fetch photo libraries:", error);
        return { status: false, data: null };
    }
}

export interface UploadPhotoParams {
    image: File;
    name: string;
    directory_id?: number;
}

export async function uploadPhotoLibrary(params: UploadPhotoParams): Promise<{ status: boolean; data: PhotoItem | null; msg?: string }> {
    try {
        const form = new FormData();
        form.append("image", params.image);
        form.append("name", params.name);
        if (params.directory_id) form.append("directory_id", String(params.directory_id));

        const res = await fetch(`${API_URL}/api/v1/photo_library`, {
            method: "POST",
            headers: authHeaders(), // không set Content-Type để trình duyệt tự thêm boundary
            body: form,
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
            if (res.status === 401) { mixLogOut(); }
            return { status: false, data: null, msg: json?.msg || `HTTP ${res.status}` };
        }
        return { status: json?.status ?? true, data: json?.data ?? null, msg: json?.msg };
    } catch (error) {
        console.error("Failed to upload photo:", error);
        return { status: false, data: null, msg: "Tải lên thất bại" };
    }
}

export async function deletePhotoLibrary(id: number): Promise<{ status: boolean; msg?: string }> {
    try {
        const res = await fetch(`${API_URL}/api/v1/photo_library/${id}`, {
            method: "DELETE",
            headers: authHeaders(),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
            if (res.status === 401) { mixLogOut(); }
            return { status: false, msg: json?.msg || `HTTP ${res.status}` };
        }
        return { status: json?.status ?? true, msg: json?.msg };
    } catch (error) {
        console.error("Failed to delete photo:", error);
        return { status: false, msg: "Xóa thất bại" };
    }
}

export interface PhotoDirectory {
    id: number;
    name: string;
}

// Danh sách thư mục con — best-effort (không có trong tài liệu file API).
// Nếu endpoint không tồn tại sẽ trả [] để UI vẫn dùng được với "Tất cả".
export async function getPhotoDirectories(): Promise<PhotoDirectory[]> {
    try {
        const res = await fetch(`${API_URL}/api/v1/photo_directories`, {
            headers: authHeaders(),
        });
        if (!res.ok) return [];
        const json = await res.json();
        const data = json?.data;
        const items = Array.isArray(data?.items)
            ? data.items
            : Array.isArray(data)
            ? data
            : [];
        return items.map((d: any) => ({ id: d.id, name: d.name }));
    } catch {
        return [];
    }
}

const jsonHeaders = () => ({ ...authHeaders(), "Content-Type": "application/json" });

export async function createPhotoDirectory(name: string): Promise<{ status: boolean; data: PhotoDirectory | null }> {
    try {
        const res = await fetch(`${API_URL}/api/v1/photo_directory`, {
            method: "POST",
            headers: jsonHeaders(),
            body: JSON.stringify({ name }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) { if (res.status === 401) mixLogOut(); return { status: false, data: null }; }
        return { status: json?.status ?? true, data: json?.data ?? null };
    } catch {
        return { status: false, data: null };
    }
}

export async function updatePhotoDirectory(id: number, name: string): Promise<{ status: boolean }> {
    try {
        const res = await fetch(`${API_URL}/api/v1/photo_directory/${id}`, {
            method: "POST",
            headers: jsonHeaders(),
            body: JSON.stringify({ name }),
        });
        if (!res.ok) { if (res.status === 401) mixLogOut(); return { status: false }; }
        const json = await res.json().catch(() => ({}));
        return { status: json?.status ?? true };
    } catch {
        return { status: false };
    }
}

export async function deletePhotoDirectory(id: number): Promise<{ status: boolean; msg?: string }> {
    try {
        const res = await fetch(`${API_URL}/api/v1/photo_directory/${id}`, {
            method: "DELETE",
            headers: authHeaders(),
        });
        const json = await res.json().catch(() => ({}));
        // Backend chỉ cho xóa khi thư mục rỗng -> trả msg lý do khi thất bại
        if (!res.ok) { if (res.status === 401) mixLogOut(); return { status: false, msg: json?.msg }; }
        return { status: json?.status ?? true, msg: json?.msg };
    } catch {
        return { status: false };
    }
}
